import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import cloudApi from "../../services/cloudApi";

// Temporarily off: new signups skip the onboarding modal. Flip to true to
// bring it back (?onboarding=preview still renders it regardless).
export const ONBOARDING_ENABLED = false;

const TOOL_OPTIONS = [
  { key: "priceMonitoring", label: "📊 Monitor prices & track competitors" },
  { key: "leadGeneration", label: "📧 Generate leads & find contact info" },
  { key: "ecommerce", label: "🛒 Collect product data from online stores" },
  { key: "mediaDownload", label: "🖼️ Download images or files in bulk" },
  { key: "listings", label: "🏠 Research listings (jobs, real estate, etc.)" },
];

const OnboardingModal = ({ preview = false }) => {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [useCase, setUseCase] = useState("");
  const [toolInterests, setToolInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleTool = (key) => {
    setToolInterests((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const otherSelected = toolInterests.includes("other");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const hasChip = toolInterests.some((k) => k !== "other");
    if (!hasChip && !useCase.trim()) return;

    // In preview mode, log to console instead of calling API
    if (preview) {
      console.log("[Onboarding Preview] Submit:", { name: name.trim(), useCase: useCase.trim(), toolInterests });
      alert(`Preview submit:\nName: ${name.trim()}\nUse case: ${useCase.trim() || "(empty)"}\nTool interests: ${toolInterests.length > 0 ? toolInterests.join(", ") : "(none)"}`);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await cloudApi.updateProfile({
        name: name.trim(),
        metadata: {
          onboarding_completed: true,
          use_case: useCase.trim() || null,
          tool_interests: toolInterests.length > 0 ? toolInterests : null,
          onboarded_at: new Date().toISOString(),
        },
      });

      updateUser({
        name: name.trim(),
        onboardingCompleted: true,
      });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-300">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 p-6 sm:p-8 shadow-2xl shadow-black/20">
          {/* Personal greeting */}
          <div className="flex items-start gap-3 mb-6 sm:mb-8">
            <div className="relative flex-shrink-0">
              <img
                src="/assets/arthur.jpeg"
                alt="Arthur"
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white dark:ring-zinc-800 shadow-sm"
                loading="lazy"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white dark:ring-zinc-900" />
            </div>
            <div className="bg-gray-50 dark:bg-white/[0.04] border border-gray-200/80 dark:border-white/[0.06] rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
              <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
                Hey, I'm Arthur — creator of Ultimate Web Scraper 👋
              </p>
              <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed mt-1.5">
                Would love to know more about your use case and how we can help you extract data.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name field */}
            <div>
              <label htmlFor="onboarding-name" className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1.5">
                What should we call you?
              </label>
              <input
                id="onboarding-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                autoFocus={!user?.name}
                className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-white dark:bg-gray-800/60 text-gray-900 dark:text-zinc-100 text-sm sm:text-base border border-gray-300/60 dark:border-gray-700/40 rounded-xl focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus-visible:outline-none placeholder-gray-400 dark:placeholder-zinc-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Your name"
              />
            </div>

            {/* Use case + tool interests */}
            <div>
              <label htmlFor="onboarding-usecase" className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                What data are you looking to collect?
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {TOOL_OPTIONS.map((tool) => {
                  const isSelected = toolInterests.includes(tool.key);
                  return (
                    <button
                      key={tool.key}
                      type="button"
                      disabled={submitting}
                      onClick={() => toggleTool(tool.key)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? "bg-blue-50 dark:bg-blue-500/15 border-blue-400 dark:border-blue-500/50 text-blue-700 dark:text-blue-300 shadow-sm"
                          : "bg-white dark:bg-gray-800/60 border-gray-300/60 dark:border-gray-700/40 text-gray-600 dark:text-zinc-400 hover:border-gray-400 dark:hover:border-gray-600/60 hover:bg-gray-50 dark:hover:bg-gray-800/80"
                      }`}
                    >
                      {tool.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => toggleTool("other")}
                  className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    otherSelected
                      ? "bg-blue-50 dark:bg-blue-500/15 border-blue-400 dark:border-blue-500/50 text-blue-700 dark:text-blue-300 shadow-sm"
                      : "bg-white dark:bg-gray-800/60 border-gray-300/60 dark:border-gray-700/40 text-gray-600 dark:text-zinc-400 hover:border-gray-400 dark:hover:border-gray-600/60 hover:bg-gray-50 dark:hover:bg-gray-800/80"
                  }`}
                >
                  Other
                </button>
              </div>
              {otherSelected && (
                <textarea
                  id="onboarding-usecase"
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  disabled={submitting}
                  rows={2}
                  className="mt-3 w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-white dark:bg-gray-800/60 text-gray-900 dark:text-zinc-100 text-sm sm:text-base border border-gray-300/60 dark:border-gray-700/40 rounded-xl focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus-visible:outline-none placeholder-gray-400 dark:placeholder-zinc-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                  placeholder="Tell us what you need..."
                />
              )}
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="bg-red-500/5 dark:bg-red-500/[0.03] border border-red-500/20 text-red-600 dark:text-red-200 px-4 py-3 rounded-xl text-xs sm:text-sm"
              >
                <p>{error}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitting || !name.trim() || (!toolInterests.some((k) => k !== "other") && !useCase.trim())}
              className="relative group w-full py-2.5 sm:py-3 px-4 sm:px-5 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-xs sm:text-sm rounded-xl hover:shadow-2xl hover:shadow-blue-400/30 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 shadow-xl border border-blue-300/50 active:scale-[0.98] overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span className="relative flex items-center justify-center gap-2 uppercase tracking-wide">
                {submitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Get Started</span>
                )}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
